import React, { useMemo, useState } from 'react';
import { List, Plus, X } from 'lucide-react';
import { useEngine, useMarketData, useTrading } from '../context/MarketContext';
import { DSE_UNIVERSE, TIER_LABEL, UNIVERSE_SIZE } from '../constants/dseUniverse';
import { resolveBySymbol, secIdFor, tickerFor } from '../constants/instruments';
import { colors, font } from '../styles/tokens';

/**
 * The counters on the desk, as a board.
 *
 * Driven by the WATCHLIST rather than the whole listed universe — same as dse-terminal's
 * Overview: you watch a handful, not all 22. Counters are added and removed here.
 *
 * KEYING: book analytics and session stats are keyed by secId; inventory and positions by
 * TICKER. Every join below goes through resolveBySymbol/secIdFor/tickerFor rather than
 * assuming — mixing the two namespaces silently returns undefined and renders as an em-dash,
 * which is exactly how the old right-rail inventory tab stayed blank for months.
 */

interface Props {
  /** The watchlist — the counters actually shown. */
  instruments: { label: string; secId: string }[];
  /** The full universe available to add. */
  allInstruments: { label: string; secId: string }[];
  onAdd: (label: string, secId: string) => void;
  onRemove: (secId: string) => void;
}

// fr-based so columns spread to the full width instead of leaving dead space on the right.
const GRID = 'minmax(78px, 0.9fr) minmax(66px, 0.7fr) minmax(74px, 0.8fr) minmax(70px, 0.8fr) minmax(84px, 0.9fr) minmax(84px, 0.9fr) minmax(58px, 0.6fr) 26px';

const DASH = '—';

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <div style={{ textAlign: align, fontSize: 9, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.07em' }}>
      {children}
    </div>
  );
}

const num = (n: number | null | undefined, dp = 2) =>
  n == null || !Number.isFinite(n) ? DASH : n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

function compact(n: number): string {
  const a = Math.abs(n), sign = n < 0 ? '-' : '';
  if (a >= 1_000_000_000) return `${sign}${(a / 1_000_000_000).toFixed(1)}B`;
  if (a >= 1_000_000) return `${sign}${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}${(a / 1_000).toFixed(0)}K`;
  return `${sign}${a.toFixed(0)}`;
}

export default function DseUniverseTable({ instruments, allInstruments, onAdd, onRemove }: Props) {
  const { selectedSymbol, setSelectedSymbol, bookAnalyticsBySecId, statsBySecId } = useMarketData();
  const { inventoryBySymbol } = useEngine();
  const { positions } = useTrading();
  const [adding, setAdding] = useState(false);

  const posByTicker = useMemo(() => {
    const m = new Map<string, typeof positions[number]>();
    for (const p of positions) m.set((p.symbol || '').toUpperCase(), p);
    return m;
  }, [positions]);

  const rows = useMemo(() => instruments.map(inst => {
    const secId = secIdFor(inst.secId);
    const ticker = tickerFor(inst.label);
    const st = statsBySecId?.get(secId) ?? null;
    const book = bookAnalyticsBySecId?.get(secId) ?? null;
    const inv = resolveBySymbol(inventoryBySymbol, ticker) ?? null;
    const pos = posByTicker.get(ticker) ?? null;
    return { inst, secId, ticker, st, book, inv, pos };
  }), [instruments, statsBySecId, bookAnalyticsBySecId, inventoryBySymbol, posByTicker]);

  const available = allInstruments.filter(a => !instruments.some(i => i.secId.toUpperCase() === a.secId.toUpperCase()));

  return (
    <div style={{ backgroundColor: colors.bg.secondary, border: `1px solid ${colors.border.subtle}`, borderRadius: 4, overflow: 'hidden' }}>

      {/* Header, in the panel idiom: amber chrome tick, wide-tracked heading, badge. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 5px',
        borderBottom: `1px solid ${colors.border.subtle}`,
        background: `linear-gradient(180deg, ${colors.soft.amber}55, transparent)`,
      }}>
        <span style={{ width: 5, height: 11, borderRadius: 1, background: colors.chrome.base, boxShadow: `0 0 6px ${colors.chrome.base}` }} />
        <List size={12} color={colors.text.secondary} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.text.primary }}>
          DSE Universe
        </span>
        <span style={{
          fontSize: 9.5, fontWeight: 600, color: colors.chrome.hi,
          background: colors.soft.amber, border: `1px solid ${colors.chrome.base}4d`, padding: '1px 7px', borderRadius: 2,
        }}>
          {instruments.length} watched · {UNIVERSE_SIZE} listed
        </span>
        <button
          onClick={() => setAdding(o => !o)}
          title={adding ? 'Close' : 'Add a counter'}
          style={{
            marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            color: adding ? colors.accent.red : colors.text.muted, display: 'flex', alignItems: 'center',
          }}
        >
          {adding ? <X size={14} /> : <Plus size={14} />}
        </button>
      </div>

      {/* Add-a-counter tray */}
      {adding && (
        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.border.subtle}`, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {available.length === 0
            ? <span style={{ fontSize: font.size.xs, color: colors.text.muted }}>Every listed counter is already on the watchlist.</span>
            : available.map(a => {
              const tier = DSE_UNIVERSE.find(c => c.ticker === a.label.toUpperCase())?.tier;
              return (
                <button
                  key={a.secId}
                  onClick={() => onAdd(a.label, a.secId)}
                  title={tier ? TIER_LABEL[tier] : undefined}
                  style={{
                    backgroundColor: colors.bg.tertiary, border: `1px solid ${colors.border.default}`,
                    borderRadius: 3, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: colors.text.primary }}>{a.label}</span>
                  <span style={{ fontSize: 9, color: colors.text.muted }}>·{a.secId}</span>
                </button>
              );
            })}
        </div>
      )}

      {/* Column heads */}
      <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '6px 10px', borderBottom: `1px solid ${colors.border.subtle}` }}>
        <Th>COUNTER</Th>
        <Th align="right">LAST</Th>
        <Th align="right">Δ%</Th>
        <Th align="right">SPREAD</Th>
        <Th align="right">NET INV</Th>
        <Th align="right">P&amp;L</Th>
        <Th align="right">TRADE</Th>
        <Th />
      </div>

      {/* Rows. Max height deliberately tight -- this is reference context, not the main surface. */}
      <div style={{ maxHeight: 232, overflowY: 'auto' }}>
        {rows.map(r => {
          const selected = selectedSymbol.toUpperCase() === r.secId.toUpperCase();
          const chg = r.st?.priceChangePercent;
          const pnl = r.pos ? (r.pos.totalPnL ?? r.pos.unrealizedPnL + r.pos.realizedPnL) : null;
          const qty = r.inv?.quantity ?? r.pos?.quantity;
          const trades = r.pos ? undefined : undefined; // trade count is not on the wire per counter

          return (
            <div
              key={r.secId}
              onClick={() => setSelectedSymbol(r.secId)}
              style={{
                display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '7px 10px',
                borderBottom: `1px solid ${colors.border.subtle}`, alignItems: 'center',
                fontSize: font.size.base, cursor: 'pointer',
                backgroundColor: selected ? `${colors.accent.blue}14` : 'transparent',
                borderLeft: `2px solid ${selected ? colors.accent.blue : 'transparent'}`,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{ color: colors.text.primary, fontWeight: 700 }}>{r.ticker}</span>
              </span>

              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: colors.text.primary }}>
                {num(r.st?.last, 0)}
              </span>

              <span style={{
                textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: chg == null ? colors.text.muted : chg >= 0 ? colors.accent.green : colors.accent.red,
              }}>
                {chg == null ? DASH : `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%`}
              </span>

              <span
                title={r.book && r.book.bestBid == null ? 'One-sided book: no resting bid, so no spread.' : undefined}
                style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: colors.text.secondary }}
              >
                {r.book?.spreadBps != null ? `${r.book.spreadBps.toFixed(0)}bps` : DASH}
              </span>

              <span style={{
                textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: qty == null ? colors.text.muted : qty > 0 ? colors.accent.green : qty < 0 ? colors.accent.red : colors.text.secondary,
              }}>
                {qty == null ? DASH : compact(qty)}
              </span>

              <span style={{
                textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                color: pnl == null ? colors.text.muted : pnl > 0 ? colors.accent.green : pnl < 0 ? colors.accent.red : colors.text.secondary,
              }}>
                {pnl == null ? DASH : compact(pnl)}
              </span>

              <span
                title="Per-counter trade count is not on the wire. fix:positionupdate carries trade_count but the WS consumer drops it."
                style={{ textAlign: 'right', color: colors.text.muted }}
              >
                {trades ?? DASH}
              </span>

              <button
                onClick={e => { e.stopPropagation(); onRemove(r.inst.secId); }}
                title={`Remove ${r.ticker} from the watchlist`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: colors.text.faint, display: 'flex', alignItems: 'center' }}
              >
                <X size={11} />
              </button>
            </div>
          );
        })}

        {rows.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: font.size.sm, color: colors.text.muted }}>
            No counters on the watchlist. Use + to add one.
          </div>
        )}
      </div>
    </div>
  );
}
